/**
 * Editor lista unita di misura per tenant.
 *
 * Caricata da Impostazioni Azienda > tab Unita di misura.
 * CRUD su /api/v1/config/unita-misura.
 *
 * Clone esatto di ModalitaPagamentoEditor.tsx.
 * Solo admin/super_admin (lato backend, qui non c'e' controllo aggiuntivo).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';

interface UnitaMisura {
  id: string;
  codice: string;
  descrizione: string;
  ordinamento: number;
  attiva: boolean;
}

interface UnitaMisuraDraft {
  codice: string;
  descrizione: string;
  ordinamento: string;
  attiva: boolean;
}

const emptyDraft = (): UnitaMisuraDraft => ({
  codice: '',
  descrizione: '',
  ordinamento: '0',
  attiva: true,
});

export const UnitaMisuraEditor: React.FC = () => {
  const token = localStorage.getItem('token') ?? '';
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [lista, setLista] = useState<UnitaMisura[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState<UnitaMisuraDraft>(emptyDraft());
  const [showNew, setShowNew] = useState(false);

  const carica = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/config/unita-misura?solo_attive=false', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      const data: UnitaMisura[] = await res.json();
      setLista(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { void carica(); }, [carica]);

  const startEdit = (u: UnitaMisura) => {
    setEditId(u.id);
    setDraft({
      codice: u.codice,
      descrizione: u.descrizione,
      ordinamento: String(u.ordinamento),
      attiva: u.attiva,
    });
    setShowNew(false);
  };

  const cancelEdit = () => {
    setEditId(null);
    setDraft(emptyDraft());
  };

  const startNew = () => {
    setEditId(null);
    setDraft(emptyDraft());
    setShowNew(true);
  };

  const validaDraft = (): string | null => {
    if (!draft.codice.trim()) return 'Codice obbligatorio';
    if (!/^[a-z0-9_]+$/.test(draft.codice)) return 'Codice: solo minuscole, numeri, underscore';
    if (!draft.descrizione.trim()) return 'Descrizione obbligatoria';
    const ord = parseInt(draft.ordinamento, 10);
    if (isNaN(ord)) return 'Ordinamento non valido';
    return null;
  };

  const salvaNuova = async () => {
    const err = validaDraft();
    if (err) { setError(err); return; }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch('/api/v1/config/unita-misura', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          codice: draft.codice.trim(),
          descrizione: draft.descrizione.trim(),
          ordinamento: parseInt(draft.ordinamento, 10),
          attiva: draft.attiva,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Errore ${res.status}`);
      }
      setSuccess('Unita di misura creata');
      setShowNew(false);
      setDraft(emptyDraft());
      await carica();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore creazione');
    } finally {
      setSaving(false);
    }
  };

  const salvaModifica = async () => {
    if (!editId) return;
    const err = validaDraft();
    if (err) { setError(err); return; }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`/api/v1/config/unita-misura/${editId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({
          codice: draft.codice.trim(),
          descrizione: draft.descrizione.trim(),
          ordinamento: parseInt(draft.ordinamento, 10),
          attiva: draft.attiva,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Errore ${res.status}`);
      }
      setSuccess('Unita di misura aggiornata');
      setEditId(null);
      setDraft(emptyDraft());
      await carica();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore aggiornamento');
    } finally {
      setSaving(false);
    }
  };

  const elimina = async (id: string, descrizione: string) => {
    if (!window.confirm(`Eliminare l'unita di misura "${descrizione}"? I documenti gia' emessi resteranno invariati.`)) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`/api/v1/config/unita-misura/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Errore ${res.status}`);
      }
      setSuccess('Unita di misura eliminata');
      await carica();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore eliminazione');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-4">Caricamento...</div>;

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-md border p-3" style={{ borderColor: 'var(--color-destructive)', color: 'var(--color-destructive)' }}>
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-md border p-3" style={{ borderColor: 'var(--color-success, #22c55e)', color: 'var(--color-success, #22c55e)' }}>
          {success}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Unita di Misura</span>
            {!showNew && !editId && (
              <Button size="sm" onClick={startNew}>Aggiungi unita</Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm" style={{ color: 'var(--color-muted-foreground)' }}>
            Lista delle unita di misura disponibili nelle righe dei documenti (preventivi, ordini, fatture).
            Le modifiche si applicano ai nuovi documenti: quelli gia' emessi conservano la dicitura originale.
          </p>

          {showNew && (
            <div className="rounded-md border p-4 space-y-3" style={{ borderColor: 'var(--color-primary, #6366f1)' }}>
              <h4 className="text-sm font-semibold">Nuova unita di misura</h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-sm">Codice (slug)</Label>
                  <Input
                    value={draft.codice}
                    onChange={(e) => setDraft((d) => ({ ...d, codice: e.target.value }))}
                    placeholder="es. mc"
                    maxLength={50}
                  />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-sm">Descrizione (mostrata)</Label>
                  <Input
                    value={draft.descrizione}
                    onChange={(e) => setDraft((d) => ({ ...d, descrizione: e.target.value }))}
                    placeholder="es. Metri cubi"
                    maxLength={200}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 items-end">
                <div className="space-y-1">
                  <Label className="text-sm">Ordinamento</Label>
                  <Input
                    type="number"
                    value={draft.ordinamento}
                    onChange={(e) => setDraft((d) => ({ ...d, ordinamento: e.target.value }))}
                  />
                </div>
                <div className="flex items-center gap-2 pb-2">
                  <input
                    type="checkbox"
                    id="new-attiva-um"
                    checked={draft.attiva}
                    onChange={(e) => setDraft((d) => ({ ...d, attiva: e.target.checked }))}
                  />
                  <Label htmlFor="new-attiva-um" className="text-sm cursor-pointer">Attiva</Label>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="ghost" onClick={() => { setShowNew(false); setDraft(emptyDraft()); }}>
                    Annulla
                  </Button>
                  <Button onClick={salvaNuova} disabled={saving}>
                    {saving ? 'Salvataggio...' : 'Crea'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {lista.length === 0 && !showNew && (
              <p className="text-sm" style={{ color: 'var(--color-muted-foreground)' }}>
                Nessuna unita di misura configurata. Clicca "Aggiungi unita" per crearne una.
              </p>
            )}
            {lista.map((u) => (
              <div
                key={u.id}
                className="rounded-md border p-3"
                style={!u.attiva ? { opacity: 0.55 } : undefined}
              >
                {editId === u.id ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-sm">Codice</Label>
                        <Input
                          value={draft.codice}
                          onChange={(e) => setDraft((d) => ({ ...d, codice: e.target.value }))}
                          maxLength={50}
                        />
                      </div>
                      <div className="col-span-2 space-y-1">
                        <Label className="text-sm">Descrizione</Label>
                        <Input
                          value={draft.descrizione}
                          onChange={(e) => setDraft((d) => ({ ...d, descrizione: e.target.value }))}
                          maxLength={200}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 items-end">
                      <div className="space-y-1">
                        <Label className="text-sm">Ordinamento</Label>
                        <Input
                          type="number"
                          value={draft.ordinamento}
                          onChange={(e) => setDraft((d) => ({ ...d, ordinamento: e.target.value }))}
                        />
                      </div>
                      <div className="flex items-center gap-2 pb-2">
                        <input
                          type="checkbox"
                          id={`attiva-um-${u.id}`}
                          checked={draft.attiva}
                          onChange={(e) => setDraft((d) => ({ ...d, attiva: e.target.checked }))}
                        />
                        <Label htmlFor={`attiva-um-${u.id}`} className="text-sm cursor-pointer">Attiva</Label>
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button variant="ghost" onClick={cancelEdit}>Annulla</Button>
                        <Button onClick={salvaModifica} disabled={saving}>
                          {saving ? 'Salvataggio...' : 'Salva'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="text-xs font-mono" style={{ color: 'var(--color-muted-foreground)' }}>
                        #{u.ordinamento}
                      </span>
                      <span className="font-medium">{u.descrizione}</span>
                      <Badge variant="outline">{u.codice}</Badge>
                      {!u.attiva && <Badge variant="secondary">disattiva</Badge>}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => startEdit(u)}>
                        Modifica
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => elimina(u.id, u.descrizione)}
                        style={{ color: 'var(--color-destructive)' }}
                      >
                        Elimina
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UnitaMisuraEditor;
